import { useCallback, useState } from 'react';

import type { ColumnDef } from './DataTable';

const STORAGE_PREFIX = 'jotbill-columns-';

interface ColumnPreferences {
  visibleKeys: string[];
  orderedColumns: ColumnDef[];
}

export function useColumnPreferences(tabKey: string, defaultColumns: ColumnDef[]) {
  const storageKey = `${STORAGE_PREFIX}${tabKey}`;

  const [state, setState] = useState<ColumnPreferences>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { visibleKeys: string[]; order: string[] };
        // Reorder defaultColumns based on stored order, keeping any new columns at the end
        const ordered = [
          ...parsed.order
            .map(key => defaultColumns.find(c => c.key === key))
            .filter((c): c is ColumnDef => c != null),
          ...defaultColumns.filter(c => !parsed.order.includes(c.key)),
        ];
        return { visibleKeys: parsed.visibleKeys, orderedColumns: ordered };
      }
    } catch {
      // ignore
    }
    return {
      visibleKeys: defaultColumns.map(c => c.key),
      orderedColumns: defaultColumns,
    };
  });

  const updatePreferences = useCallback(
    (visibleKeys: string[], orderedColumns: ColumnDef[]) => {
      setState({ visibleKeys, orderedColumns });
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            visibleKeys,
            order: orderedColumns.map(c => c.key),
          }),
        );
      } catch {
        // ignore storage errors
      }
    },
    [storageKey],
  );

  const visibleColumns = state.orderedColumns.filter(c => state.visibleKeys.includes(c.key));

  return {
    allColumns: state.orderedColumns,
    visibleKeys: state.visibleKeys,
    visibleColumns,
    updatePreferences,
  };
}
